import { ChangeEvent, Fragment, useState } from 'react'

import { IconPlus, IconListDetails, IconTrash } from '@tabler/icons-react'
import { Input, Space, Typography, List, Tag } from 'antd'
import styled from 'styled-components'

import { Button } from '../shared-components/BUI/Button'
import { Modal } from '../shared-components/BUI/Modal'

import { AdvancedDuplicate } from '@/modules/templates/templateEditor/TableEditors/AdvancedDuplicate'

import { buildPassTheme } from 'src/styles/theme'

type GridProps = {
  numColumns: number
}

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`

const Grid = styled.div<GridProps>`
  display: grid;
  grid-template-columns: 2fr ${({ numColumns }) =>
      numColumns === 1 ? '' : `repeat(${numColumns - 1}, 1fr)`} 40px;
  justify-items: right;
  align-items: stretch;
  grid-column-gap: 1rem;
  grid-row-gap: 0.5rem;
`

const Label = styled(Typography.Text).attrs({ strong: true })`
  justify-self: left;
`

const SpanCol = styled.p<GridProps>`
  grid-column-start: 1;
  grid-column-end: ${({ numColumns }) => numColumns + 1};
  margin-bottom: 0;
  align-self: center;
`

type CustomProps = {
  value: unknown
  onChange: (value: unknown) => void
}
type CustomRender = React.FC<CustomProps>

export type Preset<T> = {
  name: string
  config: T
}

export type Column = {
  name: string
  label: string
  defaultValue: string | number | null
  inputType?: 'text' | 'number'
  placeholder?: string
  render?: CustomRender
}

type Props<T extends Array<object>> = {
  buttonText: string
  modalTitle: string
  columns: Column[]
  presets?: Preset<T>[]
  existing?: {
    values: T[]
    textField: string
  }
  value: T
  onChange: (value: T) => void
  dataTestId?: string
}

export const MODAL_Z_INDEX = 200000

const Filler = () => <div />

export const ListModal = <T extends Array<object>>(props: Props<T>) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [showAdvancedDuplicate, setShowAdvancedDuplicate] = useState(false)
  const [tempValue, setTempValue] = useState(props.value || ([] as T))

  const addItem = () => {
    const emptyColumns = props.columns.reduce((agg, curr) => {
      return { ...agg, [curr.name]: curr.defaultValue }
    }, {})

    setTempValue([...tempValue, emptyColumns] as T)
  }

  const bulkAddItems = (items: string[]) => {
    const newRows = items.map((item) => {
      const emptyColumns = props.columns.reduce((agg, curr) => {
        return {
          ...agg,
          [curr.name]: curr.inputType === 'text' ? item : curr.defaultValue,
        }
      }, {})

      return emptyColumns
    }, {})

    setTempValue([...tempValue, ...newRows] as T)
  }

  const deleteItem = (index: number) => {
    setTempValue([
      ...tempValue.slice(0, index),
      ...tempValue.slice(index + 1),
    ] as T)
  }

  const setColumnValueFromEvent =
    (column: Column, index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const strValue = e.target.value
      const value = column.inputType == 'number' ? parseInt(strValue) : strValue
      setColumnValue(column, index)(value)
    }

  const setColumnValue =
    (column: Column, index: number) => (value: unknown) => {
      setTempValue(
        tempValue.map((item, i) =>
          i === index ? { ...item, [column.name]: value } : item
        ) as T
      )
    }

  const openModal = () => setModalOpen(true)
  const closeModal = () => setModalOpen(false)
  const onSave = () => {
    props.onChange(tempValue)
    closeModal()
  }

  return (
    <>
      <Button onClick={openModal} data-testid={props?.dataTestId}>
        {props.buttonText} ({tempValue?.length || 0})
      </Button>

      <Modal
        open={isModalOpen}
        title={props.modalTitle}
        okText="Save changes"
        onOk={onSave}
        onCancel={closeModal}
        width={buildPassTheme.modal.small}
        footer={
          <Footer>
            <Button
              icon={<IconListDetails size={16} />}
              onClick={() => setShowAdvancedDuplicate(true)}
            >
              Bulk add
            </Button>
            <Space>
              <Button onClick={closeModal}>Close</Button>
              <Button
                variant="primary"
                onClick={onSave}
                data-testid="save-button"
              >
                Save
              </Button>
            </Space>
          </Footer>
        }
        zIndex={MODAL_Z_INDEX}
      >
        <Grid numColumns={props.columns.length}>
          {props.columns.map((column) => (
            <Label key={column.name}>
              {tempValue.length ? column.label : ''}
            </Label>
          ))}
          <Filler data-reason="delete button" />

          {tempValue.map((value, index) => (
            <Fragment key={index}>
              {props.columns.map((column, columnIndex) => (
                <Fragment key={`${index}-${column.name}`}>
                  {column.render ? (
                    <column.render
                      value={value[column.name]}
                      onChange={setColumnValue(column, index)}
                    />
                  ) : (
                    <Input
                      type={column.inputType}
                      value={value[column.name]}
                      onChange={setColumnValueFromEvent(column, index)}
                      placeholder={column.placeholder}
                      autoFocus={
                        index === tempValue.length - 1 && columnIndex === 0
                      }
                    />
                  )}
                </Fragment>
              ))}

              <Button
                negative
                shape="circle"
                variant="secondary-text"
                icon={<IconTrash size={16} />}
                onClick={() => deleteItem(index)}
              />
            </Fragment>
          ))}

          <SpanCol numColumns={props.columns.length}>
            {tempValue.length ? '' : 'Add a new value →'}
          </SpanCol>
          <Button
            shape="circle"
            variant="secondary-text"
            icon={<IconPlus size={16} />}
            onClick={addItem}
            data-testid="add-item"
          />
        </Grid>

        {tempValue?.length === 0 && props.presets && (
          <>
            <Typography.Paragraph style={{ marginTop: '3rem' }}>
              ...or use one of our presets
            </Typography.Paragraph>
            <Space direction="horizontal">
              {props.presets.map((preset) => (
                <Button
                  key={preset.name}
                  shape="pill"
                  onClick={() => setTempValue(preset.config)}
                >
                  {preset.name}
                </Button>
              ))}
            </Space>
          </>
        )}

        {tempValue?.length === 0 && props.existing?.values?.length > 0 ? (
          <>
            <Typography.Paragraph
              type="secondary"
              style={{ marginTop: '3rem' }}
            >
              ...or copy one of your existing sets
            </Typography.Paragraph>
            <List size="small">
              {props.existing.values.map((value, i) => (
                <List.Item
                  key={i}
                  actions={[
                    <Button
                      key="copy"
                      variant="primary-text"
                      size="small"
                      onClick={() => setTempValue(value)}
                    >
                      Copy
                    </Button>,
                  ]}
                >
                  <div>
                    {value.map((v) => (
                      <Tag key={v[props.existing.textField]}>
                        {v[props.existing.textField]}
                      </Tag>
                    ))}
                  </div>
                </List.Item>
              ))}
            </List>
          </>
        ) : null}
      </Modal>

      <AdvancedDuplicate
        title="Bulk add values"
        description="Each new line below will be added as new value to the existing list"
        buttonText="Add"
        isModalOpen={showAdvancedDuplicate}
        closeModal={() => setShowAdvancedDuplicate(false)}
        onSave={bulkAddItems}
      />
    </>
  )
}
